import { anonymizeIp } from './ipAnonymizer';
import { containsPii, hasCardNumber, hasEmail, hasPhone } from './piiDetector';
import { redactTokenLikeValue } from './tokenRedactor';

const REDACTED = '[REDACTED]';

const SENSITIVE_KEY_PATTERN =
  /(password|passphrase|secret|token|authorization|cookie|api[_-]?key|refresh|session|credit|card|cvv|pin|ssn|personal[_-]?id|passport|medical|diagnosis|geo|latitude|longitude)/i;

type SanitizeOptions = {
  allowEmail?: boolean;
  maxDepth?: number;
};

function redactByKey(key: string, value: unknown, options: SanitizeOptions): unknown {
  if (SENSITIVE_KEY_PATTERN.test(key)) return REDACTED;
  if (typeof value === 'string') return sanitizeString(value, options);
  return value;
}

export function sanitizeString(input: string, options: SanitizeOptions = {}): string {
  const { allowEmail = false } = options;
  let value = redactTokenLikeValue(input);

  if (!allowEmail && hasEmail(value)) {
    value = value.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, REDACTED);
  }

  if (hasPhone(value)) {
    value = value.replace(/\+?\d[\d()\-\s]{7,}\d/g, REDACTED);
  }

  if (hasCardNumber(value)) {
    value = value.replace(/\b(?:\d[ -]*?){13,19}\b/g, REDACTED);
  }

  value = value.replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, (ip) => anonymizeIp(ip));
  return value;
}

export function sanitizeObject<T>(input: T, options: SanitizeOptions = {}, depth = 0): T {
  const maxDepth = options.maxDepth ?? 6;
  if (depth > maxDepth) return REDACTED as T;

  if (input === null || input === undefined) return input;
  if (typeof input === 'string') return sanitizeString(input, options) as T;
  if (typeof input !== 'object') return input;

  if (Array.isArray(input)) {
    return input.map((item) => sanitizeObject(item, options, depth + 1)) as T;
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    const prepared = redactByKey(key, value, options);
    if (prepared === REDACTED) {
      result[key] = REDACTED;
      continue;
    }
    result[key] = sanitizeObject(prepared, options, depth + 1);
  }
  return result as T;
}

export function sanitizeHeaders(
  headers: Record<string, string | undefined>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (!value) continue;
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      out[key] = REDACTED;
      continue;
    }
    out[key] = sanitizeString(value);
  }
  return out;
}

export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, 'http://sanitizer.local');
    const sensitiveParams = new Set([
      'token',
      'access_token',
      'refresh_token',
      'api_key',
      'apikey',
      'password',
      'email',
      'phone',
    ]);
    for (const key of [...parsed.searchParams.keys()]) {
      if (sensitiveParams.has(key.toLowerCase())) {
        parsed.searchParams.set(key, REDACTED);
      }
    }
    if (parsed.hostname === 'sanitizer.local') {
      return `${parsed.pathname}${parsed.search}`;
    }
    return parsed.toString();
  } catch {
    return sanitizeString(url);
  }
}

export function sanitizeError(error: unknown): {
  name?: string;
  message: string;
  stack?: string;
  cause?: unknown;
} {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: sanitizeString(error.message),
      stack: error.stack ? sanitizeString(error.stack) : undefined,
      cause: sanitizeObject((error as Error & { cause?: unknown }).cause),
    };
  }

  if (typeof error === 'string') {
    return { message: sanitizeString(error) };
  }

  if (error && typeof error === 'object') {
    const candidate = error as {
      name?: unknown;
      message?: unknown;
      stack?: unknown;
      cause?: unknown;
    };
    if (typeof candidate.message === 'string') {
      return {
        name: typeof candidate.name === 'string' ? sanitizeString(candidate.name) : undefined,
        message: sanitizeString(candidate.message),
        stack: typeof candidate.stack === 'string' ? sanitizeString(candidate.stack) : undefined,
        cause: sanitizeObject(candidate.cause),
      };
    }
  }

  return {
    message: 'Unknown error',
    cause: sanitizeObject(error),
  };
}

export function sanitizeUnknown<T>(value: T, options: SanitizeOptions = {}): T {
  if (typeof value === 'string' && containsPii(value)) {
    return sanitizeString(value, options) as T;
  }
  return sanitizeObject(value, options);
}
